import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { notificationsApi, NotificationAnalyticsResponse, NotificationAnalyticsParams } from '../../api/notifications';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Loader2, TrendingUp, Users, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface NotificationAnalyticsPageProps {
  isDark: boolean;
}

export default function NotificationAnalyticsPage({ isDark }: NotificationAnalyticsPageProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [analytics, setAnalytics] = useState<NotificationAnalyticsResponse | null>(null);
  const [filters, setFilters] = useState<NotificationAnalyticsParams>({});

  useEffect(() => {
    if (id) fetchAnalytics();
  }, [id, filters]);

  const fetchAnalytics = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await notificationsApi.getAnalytics(id, filters);
      setAnalytics(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch analytics');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-[#0EA5A4]" /></div>;
  }

  if (!analytics) {
    return <div className="text-center py-12"><p className={cn('text-lg', isDark ? 'text-slate-400' : 'text-slate-500')}>No analytics data found</p></div>;
  }

  const { notification, logs_summary } = analytics;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/notifications')} className={cn(isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : '')}>
          <ArrowLeft className="w-4 h-4 mr-2" />Back
        </Button>
        <div>
          <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#0F172A]')}>Notification Analytics</h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>{notification.title}</p>
        </div>
      </div>

      {/* Filters */}
      <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Start Date</Label>
              <Input type="date" value={filters.start_date || ''} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>End Date</Label>
              <Input type="date" value={filters.end_date || ''} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => setFilters({})} className={cn(isDark ? 'border-slate-600 text-white hover:bg-slate-700' : '')}>Reset Filters</Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Sent', value: notification.sent_count, icon: Users, color: isDark ? 'text-slate-600' : 'text-slate-300' },
          { label: 'Delivered', value: notification.delivered_count, rate: notification.delivery_rate, icon: CheckCircle2, color: 'text-green-500' },
          { label: 'Opened', value: notification.opened_count, rate: notification.open_rate, icon: TrendingUp, color: 'text-blue-500' },
          { label: 'Failed', value: notification.failed_count, icon: XCircle, color: 'text-red-500' },
        ].map((stat) => (
          <Card key={stat.label} className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>{stat.label}</p>
                  <p className={cn('text-2xl font-bold mt-1', isDark ? 'text-white' : 'text-[#0F172A]')}>{stat.value.toLocaleString()}</p>
                  {stat.rate !== undefined && <p className={cn('text-xs mt-1', isDark ? 'text-slate-500' : 'text-slate-400')}>{stat.rate.toFixed(1)}% rate</p>}
                </div>
                <stat.icon className={cn('w-8 h-8', stat.color)} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
          <div className="p-6">
            <h2 className={cn('text-lg font-semibold mb-4', isDark ? 'text-white' : 'text-[#0F172A]')}>Timeline</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={logs_summary.by_date}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#475569' : '#e2e8f0'} />
                <XAxis dataKey="date" tick={{ fill: isDark ? '#94a3b8' : '#64748b' }} />
                <YAxis tick={{ fill: isDark ? '#94a3b8' : '#64748b' }} />
                <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#ffffff', border: isDark ? '1px solid #475569' : '1px solid #e2e8f0', color: isDark ? '#ffffff' : '#0f172a' }} />
                <Legend />
                <Line type="monotone" dataKey="sent" stroke="#0EA5A4" name="Sent" />
                <Line type="monotone" dataKey="delivered" stroke="#10b981" name="Delivered" />
                <Line type="monotone" dataKey="opened" stroke="#3b82f6" name="Opened" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
          <div className="p-6">
            <h2 className={cn('text-lg font-semibold mb-4', isDark ? 'text-white' : 'text-[#0F172A]')}>Status Breakdown</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                { name: 'Success', value: notification.status_breakdown.success },
                { name: 'Failed', value: notification.status_breakdown.failed },
                { name: 'Invalid Token', value: notification.status_breakdown.invalid_token },
                { name: 'Not Registered', value: notification.status_breakdown.device_not_registered },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#475569' : '#e2e8f0'} />
                <XAxis dataKey="name" tick={{ fill: isDark ? '#94a3b8' : '#64748b' }} />
                <YAxis tick={{ fill: isDark ? '#94a3b8' : '#64748b' }} />
                <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#ffffff', border: isDark ? '1px solid #475569' : '1px solid #e2e8f0', color: isDark ? '#ffffff' : '#0f172a' }} />
                <Bar dataKey="value" fill="#0EA5A4" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Details */}
      <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
        <div className="p-6">
          <h2 className={cn('text-lg font-semibold mb-4', isDark ? 'text-white' : 'text-[#0F172A]')}>Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Created</p>
              <p className={cn('font-medium mt-1', isDark ? 'text-white' : 'text-[#0F172A]')}>{format(new Date(notification.created_at), 'PPpp')}</p>
            </div>
            {notification.scheduled_at && (
              <div>
                <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Scheduled</p>
                <p className={cn('font-medium mt-1', isDark ? 'text-white' : 'text-[#0F172A]')}>{format(new Date(notification.scheduled_at), 'PPpp')}</p>
              </div>
            )}
            {notification.sent_at && (
              <div>
                <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Sent</p>
                <p className={cn('font-medium mt-1', isDark ? 'text-white' : 'text-[#0F172A]')}>{format(new Date(notification.sent_at), 'PPpp')}</p>
              </div>
            )}
            {notification.estimated_recipients && (
              <div>
                <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Estimated Recipients</p>
                <p className={cn('font-medium mt-1', isDark ? 'text-white' : 'text-[#0F172A]')}>{notification.estimated_recipients.toLocaleString()}</p>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
